import { useTranslation } from 'react-i18next';
import { Link, useParams } from 'react-router-dom';
import { useCanAccess } from '@permissions/useFeatures';
import { DataUnavailable } from '@components/DataUnavailable/DataUnavailable';
import { AlertSettingsList } from '@components/AlertSettings/AlertSettingsList';
import { AlertSettingsLoading } from '@components/AlertSettings/AlertSettingsLoading';
import { rowChangeHandlers } from '@components/AlertSettings/rowChangeHandlers';
import { PageActions } from '@components/ui/PageFrame/PageFrame';
import { usePersonalAlertSettings, useUpdatePersonalAlertSetting } from './hooks/usePersonalAlertSettings';
import styles from './styles.module.css';

/**
 * Account settings → Alert settings, per project (`/settings/alerts/:siteId`):
 * the person's own channels and frequencies, on the same cards and rows as
 * the Alerts Hub but without recipients. Restricted areas are listed
 * read-only — creating and editing them is the Hub's. Every role.
 */
export function AlertSettingsPage() {
  const { t } = useTranslation();
  // The route always names the site.
  const { siteId } = useParams() as { siteId: string };
  const canManage = useCanAccess('admin');
  const settings = usePersonalAlertSettings(siteId);
  const save = useUpdatePersonalAlertSetting(siteId);

  return (
    <div className={styles.page}>
      {canManage && (
        <PageActions>
          <Link className={styles.hubLink} to={`/admin/${encodeURIComponent(siteId)}?tab=alertsHub`}>
            {t('alertSettings.personal.manageHub')}
          </Link>
        </PageActions>
      )}
      {settings.isError ? (
        <DataUnavailable />
      ) : !settings.data ? (
        <AlertSettingsLoading />
      ) : (
        <AlertSettingsList
          groups={settings.data.groups}
          rowProps={(row) => rowChangeHandlers((update) => save.mutate({ key: row.key, update }))}
        />
      )}
    </div>
  );
}
